import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import CheckboxSelect from '@/components/Base/CheckboxSelect';
import Icon from '@/components/Base/Icon';
import Input from '@/components/Base/Input';
import Select from '@/components/Base/Select';
import SelectMultiple from '@/components/Base/Select/SelectMultiple/SelectMultiple';
import FiltersMoreButton from '@/components/Pages/Home/ProductsFiltersForm/FiltersMoreButton';
import { DEFAULT_CURRENCY } from '@/constants';
import { FORM_ITEMS } from '@/constants/filter';
import {
  useGetProductsWithCompany,
  useHiddenFilters,
  useLabeledOptions,
  usePageProps,
  useQueryUpdater,
} from '@/hooks';

import AmountInput from './AmountInput';
import {
  ClearFiltersButton,
  GridItem,
  HiddenFilters,
  MainFilters,
  ProductsCount,
  SubmitBtn,
  Wrapper,
} from './ProductsFiltersForm.styles';

export const TERM_VALUES = [
  null,
  1,
  2,
  3,
  6,
  9,
  12,
  24,
  36,
  48,
  60,
  72,
  84,
  96,
  108,
  120,
  144,
  180,
  240,
  300,
  1200,
];
export const PURPOSE_VALUES = [
  'circulating_assets',
  'purchase_of_vehicles',
  'real_estate_purchase',
  'purchase_of_special_equipment',
  'purchase_of_equipment',
  'energy_efficiency',
  'business_development',
  'refinancing',
  'guarantee',
  'factoring',
  'avalizing_bills',
  'letter_of_credit',
  'encashment',
  'investment_goals',
  'export_execution_costs',
];

let submitTimeout = null;

const getRouterParams = (params) =>
  FORM_ITEMS.reduce(
    (res, cur) => ({
      ...res,
      [cur.id]:
        cur.type === 'miltySelect'
          ? params?.[cur.id]?.split(',')
          : params?.[cur.id],
    }),
    {}
  );

const isFiltersIconActive = (filters) =>
  Object.values(filters).some(
    (value) => value !== undefined && value !== null && value !== ''
  );

function ProductsFiltersForm() {
  const { locale, formatMessage } = useIntl();
  const { params, location } = usePageProps();
  const { count: productsCount } = useGetProductsWithCompany({ locale });
  const isCompanyPage = !!params?.companySlug;

  const [isOpenHiddenMenu, setOpenHiddenMenu] = useState(false);
  const [values, setValues] = useState(getRouterParams(params));

  const didMountCurrent = useRef(false);

  const termOptions = useLabeledOptions(TERM_VALUES, 'term');
  const purposeOptions = useLabeledOptions(PURPOSE_VALUES, 'purpose');
  const { hiddenElements = [], checkboxList = [] } = useHiddenFilters();
  const { setMultipleQueryParams } = useQueryUpdater();
  const filtersNotEmpty = isFiltersIconActive(values);

  const onSubmit = (data) => {
    const formattedValues = Object.keys(data).reduce((result, item) => {
      let currentValue = data[item];

      // Не додаємо в квері параметри дофолтне значення валюти
      if (item === 'currency' && currentValue === DEFAULT_CURRENCY) {
        currentValue = null;
      }

      return {
        ...result,
        [item]: Array.isArray(currentValue)
          ? currentValue.join(',')
          : currentValue,
      };
    }, {});

    setMultipleQueryParams({
      values: formattedValues,
      pathnamePriority:
        isCompanyPage && data.vendor_id
          ? `${process.env.APP_ROUTER_BASENAME}/`
          : undefined,
    });
  };

  const scrollToProductsList = (e) => {
    e.preventDefault();
    document?.getElementById('mc-products-list').scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const handleInputChange = useCallback(
    (value, name) => {
      clearTimeout(submitTimeout);

      setValues((prev) => ({
        ...prev,
        [name]: value,
      }));
      submitTimeout = setTimeout(
        () => onSubmit({ ...values, [name]: value }),
        400
      );

      return () => {
        clearTimeout(submitTimeout);
      };
    },
    [submitTimeout, values]
  );

  const handleMoreButtonClick = useCallback(
    (e) => {
      e.preventDefault();
      setOpenHiddenMenu(!isOpenHiddenMenu);
    },
    [isOpenHiddenMenu]
  );

  const handleSelectChange = useCallback(
    ({ key, value }) => {
      clearTimeout(submitTimeout);

      setValues((prev) => ({
        ...prev,
        [key]: value,
      }));
      onSubmit({ ...values, [key]: value });
    },
    [submitTimeout, values]
  );

  const handleSelectMultipleChange = useCallback(
    ({ key, values: newValues }) => {
      clearTimeout(submitTimeout);

      setValues((prev) => ({
        ...prev,
        [key]: newValues?.length ? newValues : null,
      }));
      onSubmit({
        ...values,
        [key]: newValues?.length ? newValues.join(',') : null,
      });
    },
    [submitTimeout, values, location]
  );

  const handleChacked = useCallback(
    ({ key, checked }) => {
      const value = !checked ? '1' : '';

      setValues((prev) => ({
        ...prev,
        [key]: value,
      }));
      onSubmit({ ...values, [key]: value });
    },
    [values]
  );

  const onFiltersClear = (e) => {
    e.preventDefault();
    setValues({});
    setMultipleQueryParams({
      values: getRouterParams({}),
    });
  };

  useEffect(() => {
    const newValues = getRouterParams(params);
    const isNotNewEmptyFilters = Object.values(newValues).some(
      (item) => !!item
    );
    const isNotEmptyFilters = Object.values(values).some((item) => !!item);

    if (didMountCurrent.current && !isNotNewEmptyFilters && isNotEmptyFilters) {
      setValues(newValues);
    } else {
      didMountCurrent.current = true;
    }
  }, [params]);

  return (
    <Wrapper noValidate>
      <MainFilters>
        <GridItem $area="main-1">
          <AmountInput
            name="amount"
            amount={values?.amount}
            currency={values?.currency}
            handleInputChange={handleInputChange}
            handleSelectChange={handleSelectChange}
          />
        </GridItem>

        <GridItem $area="main-2">
          <Select
            type="radio"
            name="term"
            options={termOptions}
            value={values?.term}
            onChange={handleSelectChange}
            placeholder={formatMessage({
              id: 'productFiltersForm.term.placeholder',
            })}
          />
        </GridItem>

        <GridItem $area="main-3">
          <SelectMultiple
            name="purpose"
            options={purposeOptions}
            defaultValues={values?.purpose}
            onChange={handleSelectMultipleChange}
            placeholder={formatMessage({
              id: 'productFiltersForm.purpose.placeholder',
            })}
          />
        </GridItem>

        <GridItem $area="main-4">
          <FiltersMoreButton
            isActive={filtersNotEmpty}
            handleClick={handleMoreButtonClick}
            data-active={isOpenHiddenMenu}
          />

          <SubmitBtn type="submit" onClick={scrollToProductsList}>
            {formatMessage({
              id: 'productFiltersForm.result',
            })}

            <ProductsCount>{productsCount}</ProductsCount>
          </SubmitBtn>
        </GridItem>
      </MainFilters>

      {isOpenHiddenMenu && (
        <HiddenFilters $filtersNotEmpty={filtersNotEmpty}>
          <GridItem $area="hd-0">
            {checkboxList.map(({ id, label }) => (
              <CheckboxSelect
                key={id}
                name={id}
                label={label || null}
                value={values?.[id]}
                isSelected={!!values?.[id]}
                onChange={handleChacked}
              />
            ))}
          </GridItem>

          {hiddenElements.map(({ id, options, type }, index) => (
            <GridItem key={id} $area={`hd-${index + 1}`}>
              {type === 'miltySelect' && (
                <SelectMultiple
                  name={id}
                  options={options}
                  defaultValues={values?.[id]}
                  onChange={handleSelectMultipleChange}
                  placeholder={formatMessage({
                    id: `productFiltersForm.${id}.placeholder`,
                  })}
                  autocompletePlaceholder={formatMessage({
                    id: `productFiltersForm.${id}.placeholder`,
                  })}
                />
              )}

              {type === 'select' && (
                <Select
                  type="radio"
                  name={id}
                  options={options}
                  value={values?.[id]}
                  onChange={handleSelectChange}
                  placeholder={formatMessage({
                    id: `productFiltersForm.${id}.placeholder`,
                  })}
                />
              )}

              {type === 'input' && (
                <Input
                  id={id}
                  name={id}
                  value={values?.[id]}
                  onChange={handleInputChange}
                  placeholder={formatMessage({
                    id: `productFiltersForm.${id}.placeholder`,
                  })}
                  type="number"
                />
              )}
            </GridItem>
          ))}

          {filtersNotEmpty && (
            <GridItem $area="hd-11">
              <ClearFiltersButton onClick={(e) => onFiltersClear(e)}>
                <Icon name="reload" width="15" height="15" />

                {formatMessage({
                  id: 'productFiltersForm.clearFiltersButton',
                })}
              </ClearFiltersButton>
            </GridItem>
          )}
        </HiddenFilters>
      )}
    </Wrapper>
  );
}

export default ProductsFiltersForm;
